
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { toast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';

interface SupervisionSession {
  id: string;
  supervisor_name: string;
  date: string;
  duration: number;
  topics: string[];
  notes: string;
  follow_up?: string;
}

export const SupervisionTable = () => {
  const [sessions, setSessions] = useState<SupervisionSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchSessions = async () => {
    try {
      console.log('Fetching supervision sessions...');
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('supervision_sessions')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;
      
      console.log('Supervision sessions data:', data);
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching supervision sessions:', error);
      setError('Could not load supervision sessions');
      toast({
        title: 'Error',
        description: 'Could not load supervision sessions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from('supervision_sessions')
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supervision session deleted successfully',
      });
      
      fetchSessions();
    } catch (error) {
      console.error('Error deleting supervision session:', error);
      toast({
        title: 'Error',
        description: 'Could not delete supervision session',
        variant: 'destructive',
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleEdit = (sessionId: string) => {
    console.log(`Editing session ${sessionId}`);
    navigate(`/supervision/edit/${sessionId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coaching-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-md shadow p-6">
        <div className="text-center text-red-500 p-4">
          <p>{error}</p>
          <Button 
            onClick={fetchSessions} 
            variant="outline" 
            className="mt-4"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-md shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Supervisor</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Topics</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No supervision sessions found. Click "Add Session" to create one.
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session) => (
                <TableRow key={session.id} className="hover:bg-gray-50">
                  <TableCell>{format(new Date(session.date), 'PPP')}</TableCell>
                  <TableCell>{session.supervisor_name}</TableCell>
                  <TableCell>{session.duration} minutes</TableCell>
                  <TableCell>{session.topics?.join(', ') || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(session.id)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteId(session.id)}
                        className="h-8 w-8 p-0 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the supervision session.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
